import Ellipsis from "components/Ellipsis";
import SpinnerWithLoadMask from "components/SpinnerWithLoadMask";
import { INITIAL_PAGE, ITEMS_PER_PAGE } from "constants/app";
import { checkedTypes } from "constants/types";
import { useColumnsResizing } from "hooks/generic/useColumnsResizing";
import parse from "html-react-parser";
import { folderBy } from "layout/dictionarySearch/constants";
import ContentHeader from "layout/dictionarySearch/content/ContentHeader";
import {
  bold,
  DictionarySearchEmptyContent,
  DictionarySearchEmptyContentNoOccurrences,
} from "layout/dictionarySearch/content/helpers/renderHelper";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getContent } from "slices/dictionarySearch/actions/content";
import {
  resetDictionarySearch,
  setContentData,
} from "slices/dictionarySearch/dictionarySearchSlice";
import {
  selectConfigFolderBy,
  selectCurrentDocuments,
  selectSelectedDocuments,
  selectSelectedTermInstances,
} from "slices/dictionarySearch/selectors";
import { getImage, scrollToTop } from "utils";

export const getDocItemsArrayList = (documents) => {
  if (documents.reduce === undefined) {
    return [];
  }
  return documents.reduce((result, doc) => {
    const terms = [];
    doc.dictionary?.categories?.forEach((cat) => {
      if (cat.items) {
        cat.items.forEach((item) => {
          if (item.checkState === checkedTypes.CHECKED) {
            terms.push(item.id);
          }
        });
      }
    });
    if (terms.length) {
      result.push({
        docName: doc.name,
        selectedTerms: terms,
      });
    }

    return result;
  }, []);
};

export const getDocIdItemsArrayList = (by, checkedItems, documents) => {
  if (by === folderBy.folder) {
    return documents
      ?.filter((d) => d.checkState === checkedTypes.CHECKED)
      .map((d) => ({
        docId: d.id,
        selectedTerms: checkedItems,
      }));
  }
  return documents.reduce((result, doc) => {
    const terms = [];
    doc.dictionary.categories.forEach((cat) => {
      if (cat.items) {
        cat.items.forEach((item) => {
          if (item.checkState === checkedTypes.CHECKED) {
            terms.push(item.id);
          }
        });
      }
    });

    result.push({
      docId: doc.id,
      selectedTerms: terms.length ? terms : [],
    });

    return result;
  }, []);
};

const getColumnWidths = (docView) => {
  let columnWidths = [];

  if (!docView) {
    columnWidths.push(115);
  }

  const rest = [100, 90, 90, "auto"];

  columnWidths = columnWidths.concat(rest);

  return columnWidths;
};

const getMinWidths = (docView) => {
  let minWidths = [];

  if (!docView) {
    minWidths.push(65);
  }

  const rest = [65, 65, 65, 300];

  minWidths = minWidths.concat(rest);

  return minWidths;
};

export const SS_KEY_FOLDER_BY_FOLDER =
  "dictionary-search-folder-by-folder-settings";
export const SS_KEY_FOLDER_BY_DOCUMENT =
  "dictionary-search-folder-by-document-settings";
export const SS_KEY_DOCUMENT = "dictionary-search-document-settings";

const getSSKey = (docView, byFolder) => {
  if (docView) {
    return SS_KEY_DOCUMENT;
  }
  return byFolder ? SS_KEY_FOLDER_BY_FOLDER : SS_KEY_FOLDER_BY_DOCUMENT;
};

const ContentPanel = ({ docView }) => {
  const { data } = useSelector((state) => state.dictionarySearch.content);
  const [displayedData, setDisplayedData] = useState([]);
  const { folderId } = useParams();
  const by = useSelector(selectConfigFolderBy);
  const selectedDocuments = useSelector(selectSelectedDocuments);
  const currentDocuments = useSelector(selectCurrentDocuments);
  const [selectedTerms, totalOccurances] = useSelector(
    selectSelectedTermInstances
  );
  const bodyRef = useRef();
  const headerRef = useRef();
  const dispatch = useDispatch();

  const { columnWidths, onColumnResize } = useColumnsResizing(
    getColumnWidths(docView),
    getMinWidths(docView),
    getSSKey(docView, by === folderBy.folder),
    headerRef
  );

  const docColumn = (doc, width) => {
    return (
      <span style={{ width }} className="content__body__row__document">
        <img src={getImage(doc.docName)} alt="doc-img" />
        <span title={doc.docName} data-clarity-mask="True">{doc.docName}</span>
      </span>
    );
  };

  const handlePageChange = ([from]) => {
    setDisplayedData([...data].splice(from, ITEMS_PER_PAGE));
    scrollToTop(bodyRef);
  };

  useEffect(() => {
    if (by && folderId && selectedTerms.length > 0) {
      dispatch(getContent({ folderId, by, selectedDocuments, selectedTerms }));
    } else {
      dispatch(setContentData([]));
    }
  }, [by, folderId, selectedDocuments, selectedTerms]);

  useEffect(() => {
    return () => {
      dispatch(resetDictionarySearch());
    };
  }, []);

  const showEmptyContent = () =>
    totalOccurances > 0
      ? DictionarySearchEmptyContent()
      : DictionarySearchEmptyContentNoOccurrences();

  const getRows = () => {
    /* First column is optional depends on view. So array with sizes can also contain different amount of values.
    Because of this we assign sizes from last to first column. To do this need to reverse array with sizes */
    const reversedColumnWidths = [...columnWidths];
    reversedColumnWidths.reverse();

    return displayedData.map((d) => (
      <div className="content__body__row">
        {!docView && docColumn(d, reversedColumnWidths[4])}
        <span
          style={{ width: reversedColumnWidths[3] }}
          className="content__body__row__terms"
        >
          {d.legend.map((l) => (
            <Ellipsis text={l} />
          ))}
        </span>
        <span
          style={{ width: reversedColumnWidths[2] }}
          className="content__body__row__location"
        >
          {d.location}
        </span>
        <span
          style={{ width: reversedColumnWidths[1] }}
          className="content__body__row__ref"
        >
          {d.ref}
        </span>
        <span
          style={{ width: reversedColumnWidths[0] }}
          className="content__body__row__body"
        >
          {parse(bold(selectedTerms, d.text))}
        </span>
      </div>
    ));
  };

  useEffect(() => {
    if (data.length) {
      handlePageChange([0]);
    }
  }, [data]);

  const handleScroll = (e) => {
    if (headerRef.current && bodyRef.current) {
      const { scrollLeft } = e.target;
      headerRef.current.scrollLeft = scrollLeft;
    }
  };

  return (
    <div className="content" role="presentation" data-testid="content-pane">
      <ContentHeader
        documentId={docView ? 1 : 0}
        docItemsLength={currentDocuments?.length}
        paginationConfig={{
          numberOfItems: data?.length || 0,
          itemsPerPage: ITEMS_PER_PAGE,
          initialPage: INITIAL_PAGE,
          onPageChange: handlePageChange,
        }}
        columnWidths={columnWidths}
        onResize={onColumnResize}
        headerRef={headerRef}
      />
      <div ref={bodyRef} className="content__body" onScroll={handleScroll}>
        <SpinnerWithLoadMask opacity={0.7} fullScreen={false} />
        {data?.length > 0 && currentDocuments?.length
          ? getRows()
          : showEmptyContent()}
      </div>
    </div>
  );
};

export default ContentPanel;

ContentPanel.propTypes = {
  docView: PropTypes.bool,
};
